import { useState } from "react"
import { Icon } from "#app/components/ui/icon.tsx"
import { Button } from "#app/components/ui/button.tsx"
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "#app/components/ui/tooltip.tsx"
import { BASE_URL } from "#app/utils/constants"
import { useUmami } from "#app/utils/umami"
import { AnalyticsEvents } from "#app/utils/analytics"

export function ShareButtons({
	url,
	title,
	description,
}: {
	url: string
	title: string
	description?: string
}) {
	const [copied, setCopied] = useState(false)
	const { umami } = useUmami()

	const fullUrl = url.startsWith("http") ? url : `${BASE_URL}${url}`
	const encodedUrl = encodeURIComponent(fullUrl)
	const encodedTitle = encodeURIComponent(title)
	const encodedDescription = encodeURIComponent(description ?? title)

	const trackShare = (platform: string) => {
		umami().track(AnalyticsEvents.SOCIAL_SHARE, {
			platform,
			url: fullUrl,
			title,
		})
	}

	const handleCopyLink = async () => {
		await navigator.clipboard.writeText(fullUrl)
		setCopied(true)
		trackShare("copy_link")
		setTimeout(() => setCopied(false), 2000)
	}

	const shareLinks = [
		{
			name: "Facebook",
			icon: "share-facebook" as const,
			href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
		},
		{
			name: "X",
			icon: "share-x" as const,
			href: `https://x.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
		},
		{
			name: "WhatsApp",
			icon: "share-whatsapp" as const,
			href: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
		},
		{
			name: "Pinterest",
			icon: "share-pinterest" as const,
			href: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodedDescription}`,
		},
		{
			name: "Email",
			icon: "envelope-closed" as const,
			href: `mailto:?subject=${encodedTitle}&body=${encodedDescription}%0A%0A${encodedUrl}`,
		},
	]

	return (
		<TooltipProvider>
			<div className="flex items-center gap-1">
				<span className="mr-1 text-sm text-muted-foreground">Compartir</span>
				{shareLinks.map(link => (
					<Tooltip key={link.name}>
						<TooltipTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								className="h-9 w-9"
								asChild
							>
								<a
									href={link.href}
									target={link.name === "Email" ? "_self" : "_blank"}
									rel="noopener noreferrer"
									onClick={() => trackShare(link.name.toLowerCase())}
									aria-label={`Compartir en ${link.name}`}
								>
									<Icon name={link.icon} size="md" />
								</a>
							</Button>
						</TooltipTrigger>
						<TooltipContent>{link.name}</TooltipContent>
					</Tooltip>
				))}
				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							variant="ghost"
							size="icon"
							className="h-9 w-9"
							onClick={handleCopyLink}
							aria-label="Copiar enlace"
						>
							<Icon
								name={copied ? "check" : "link-2"}
								size="md"
							/>
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						{copied ? "Enlace copiado" : "Copiar enlace"}
					</TooltipContent>
				</Tooltip>
			</div>
		</TooltipProvider>
	)
}
